import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, Checkbox, Divider, Flex, FormLabel, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Progress, Select, Stack, Text, Textarea, useToast,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { v4 as uuid } from 'uuid';
import useProjects from '../hooks/useProjects';
import useWorkOrders from '../hooks/useWorkOrders'; // item 881 — the required WO anchor
import useUsers from '../hooks/useUsers';
import { UserDto } from '../models/user';
import { Project } from '../models/project';
import { RecurrenceFrequency, frequencyLabel } from '../models/recurringRevenue';
import useRecurringRevenueCreate, {
  RecurringLineInput, RecurringEarnerInput,
} from '../hooks/useRecurringRevenueCreate';
import { money } from '../utils/money';
import CatalogCombobox from './forms/CatalogCombobox'; // item 878 — catalog search in the SKU cell
import { bindingLinePrice } from '../models/item'; // item 878 — seed the monthly amount

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // item 462a — when the wizard opens from a project's Subscriptions section, the
  // project is preselected and LOCKED (create without leaving the project)
  lockedProjectId?: string;
}

const STEPS = ['Basics', 'Line items', 'Commissions'] as const;

const blankLine = (): RecurringLineInput => ({
  clientKey: uuid(), sku: '', brand: '', description: '', monthlyAmount: 0,
});

// item 448 — the guided "Add recurring invoice" wizard: the ONE place recurring
// revenue AND recurring commissions are created. Manual by design — no auto-magic.
const AddRecurringInvoiceWizard: React.FC<Props> = ({ isOpen, onClose, lockedProjectId }) => {
  const toast = useToast();
  const { data: projects } = useProjects();
  const { data: users } = useUsers();
  const create = useRecurringRevenueCreate();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState(lockedProjectId ?? '');
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(RecurrenceFrequency.Monthly);
  const [startDate, setStartDate] = useState('');
  const [lines, setLines] = useState<RecurringLineInput[]>([blankLine()]);
  // item 878 (Chad) — optional header/footer notes for every monthly invoice
  const [headerNote, setHeaderNote] = useState('');
  const [footerNote, setFooterNote] = useState('');
  // item 881 (Chad: "make it rigid") — the WORK ORDER this subscription bills under (required)
  const [workOrderId, setWorkOrderId] = useState('');
  const { data: workOrdersData } = useWorkOrders();
  // item 883 (Chad) — end date: '' = forever; a date becomes Stripe's cancel_at
  const [endMode, setEndMode] = useState<'never' | 'date'>('never');
  const [endDate, setEndDate] = useState('');
  const [earners, setEarners] = useState<RecurringEarnerInput[]>([]);
  // item 1360 (Chad: "a manual add may not have the exact SKU — we need a tick to verify this is
  // RNWY/SP/HD") — the products this sub delivers, ticked by the person creating it
  const [prodRnwy, setProdRnwy] = useState(false);
  const [prodSp, setProdSp] = useState(false);
  const [prodHd, setProdHd] = useState(false);
  const [prodHdTier, setProdHdTier] = useState(0);
  const [prodTp, setProdTp] = useState(false);
  // item 1479 (Chad: "I am adding a sub and it requires a RNWY flag. We need a generic flag non-RNWY")
  // — a NON-RNWY subscription (e.g. Press Play / Club Spa cloud services) is an explicit,
  // verified "none of the RNWY products": the sub exists, RNWY is entitled to nothing. Same
  // answer the edit modal's "None of these" records (grid badge NONE ✓).
  const [prodNone, setProdNone] = useState(false);
  const anyProduct = prodRnwy || prodSp || prodHd || prodTp || prodNone;
  const productsOk = anyProduct && (!prodHd || (prodHdTier >= 1 && prodHdTier <= 3));

  const reset = () => {
    setStep(0); setName(''); setProjectId(lockedProjectId ?? '');
    setFrequency(RecurrenceFrequency.Monthly); setStartDate('');
    setLines([blankLine()]); setEarners([]);
  };
  // item 462a — a late-arriving lock (section mounts before the wizard opens) still applies
  React.useEffect(() => { if (lockedProjectId) setProjectId(lockedProjectId); }, [lockedProjectId]);
  const close = () => { reset(); onClose(); };

  const validLines = lines.filter((l) => l.monthlyAmount > 0 && (l.sku.trim() || l.description.trim()));
  const monthlyTotal = validLines.reduce((s, l) => s + (Number(l.monthlyAmount) || 0), 0);
  const lineLabel = (l: RecurringLineInput) => [l.brand, l.sku].filter(Boolean).join(' ') || l.description || 'Line';

  const setLine = (key: string, patch: Partial<RecurringLineInput>) => setLines(
    (prev) => prev.map((l) => (l.clientKey === key ? { ...l, ...patch } : l)),
  );
  const setEarner = (idx: number, patch: Partial<RecurringEarnerInput>) => setEarners(
    (prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
  );

  const earnerMonthly = (e: RecurringEarnerInput): number => {
    const line = validLines.find((l) => l.clientKey === e.lineClientKey);
    const base = line ? Number(line.monthlyAmount) || 0 : monthlyTotal;
    return Math.round(base * (Number(e.percent) || 0)) / 100;
  };

  // item 881 — live work orders on the chosen project (the anchor is required)
  const projectWorkOrders = useMemo(
    () => (workOrdersData?.index ?? []).filter(
      (w: any) => w.projectId === projectId && !w.isVoid && !w.isArchive && !w.isArchived,
    ),
    [workOrdersData, projectId],
  );

  const canNext = useMemo(() => {
    if (step === 0) {
      return name.trim().length > 0 && !!projectId
        && !!workOrderId // item 881 — no anchor, no subscription
        && (endMode === 'never' || !!endDate); // item 883 — a dated end needs its date
    }
    if (step === 1) return validLines.length > 0 && productsOk; // item 1360 — products are required
    return true;
  }, [step, name, projectId, workOrderId, endMode, endDate, validLines.length, productsOk]);

  const submit = async () => {
    const project = (projects?.index ?? []).find((p: Project) => p.id === projectId);
    if (!project) { toast({ status: 'error', title: 'Pick a project.' }); return; }
    // earners must reference a real line + carry a positive %
    const cleanEarners = earners.filter(
      (e) => e.userId && (Number(e.percent) || 0) > 0
        && validLines.some((l) => l.clientKey === e.lineClientKey),
    );
    try {
      await create.mutateAsync({
        name: name.trim(),
        projectId,
        customerId: project.customerId,
        frequency,
        startDate: startDate || null,
        workOrderId: workOrderId || undefined, // item 881 — the WO it bills under
        endDate: endMode === 'date' && endDate ? endDate : null, // item 883 — null = forever
        headerNote: headerNote.trim() || undefined, // item 878
        footerNote: footerNote.trim() || undefined, // item 878
        lines: validLines.map((l) => ({ ...l, monthlyAmount: Number(l.monthlyAmount) || 0 })),
        earners: cleanEarners.map((e) => ({ ...e, percent: Number(e.percent) || 0 })),
        // item 1360 — explicit, creator-verified product flags (feeds the RNWY entitlements API)
        products: {
          isRnwy: prodRnwy, isSwitchPilot: prodSp, isHelpDesk: prodHd, helpDeskTier: prodHd ? prodHdTier : 0, isThirdParty: prodTp, verified: true,
        },
      });
      toast({ status: 'success', title: 'Recurring invoice created', description: `${money(monthlyTotal)}/mo · ${cleanEarners.length} commission${cleanEarners.length === 1 ? '' : 's'}` });
      close();
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not create', description: e?.response?.data ?? 'Try again.' });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="3xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add recurring invoice</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="xs" color="gray.500" mb={2}>{`Step ${step + 1} of 3 — ${STEPS[step]}`}</Text>
          <Progress value={((step + 1) / 3) * 100} size="xs" colorScheme="brandGreen" borderRadius="full" mb={5} />

          {step === 0 && (
            <Stack spacing={4}>
              <Box>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Managed Services — Monthly" />
              </Box>
              <Box>
                <FormLabel fontSize="sm">Project</FormLabel>
                <Select
                  placeholder="— Select project —"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  isDisabled={!!lockedProjectId}
                >
                  {(projects?.index ?? []).map((p: Project) => (
                    <option key={p.id} value={p.id}>{p.customerName ? `${p.name} — ${p.customerName}` : p.name}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {lockedProjectId
                    ? 'Locked to this project — the customer is taken from it.'
                    : 'The customer is taken from the project.'}
                </Text>
              </Box>
              {/* item 881 (Chad) — RIGID: the subscription anchors to the work order it bills
                  under. No live work order on the project → create one first. */}
              {projectId && (
                <Box>
                  <FormLabel fontSize="sm">Work order</FormLabel>
                  {projectWorkOrders.length > 0 ? (
                    <>
                      <Select
                        placeholder="— Select work order —"
                        value={workOrderId}
                        onChange={(e) => setWorkOrderId(e.target.value)}
                      >
                        {projectWorkOrders.map((w: any) => (
                          <option key={w.id} value={w.id}>
                            {[w.workOrderNumber, w.name].filter(Boolean).join(' — ')}
                          </option>
                        ))}
                      </Select>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        The job this subscription bills under — it prints on every monthly invoice.
                      </Text>
                    </>
                  ) : (
                    <Text fontSize="sm" color="orange.300" borderWidth="1px" borderStyle="dashed" borderColor="orange.300" borderRadius="md" p={3}>
                      This project has no live work orders yet — create the work order first
                      (Project → Work Orders), then come back and add the subscription.
                    </Text>
                  )}
                </Box>
              )}
              <HStack spacing={4} align="flex-end">
                <Box flex="1">
                  <FormLabel fontSize="sm">Frequency</FormLabel>
                  <Select value={frequency} onChange={(e) => setFrequency(Number(e.target.value))}>
                    {[RecurrenceFrequency.Monthly, RecurrenceFrequency.Quarterly, RecurrenceFrequency.Annual].map((f) => (
                      <option key={f} value={f}>{frequencyLabel(f)}</option>
                    ))}
                  </Select>
                </Box>
                <Box flex="1">
                  <FormLabel fontSize="sm">Start date</FormLabel>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </Box>
              </HStack>
              {/* item 883 (Chad) — end date or forever, just like Stripe (cancel_at) */}
              <HStack spacing={4} align="flex-end">
                <Box flex="1">
                  <FormLabel fontSize="sm">Ends</FormLabel>
                  <Select value={endMode} onChange={(e) => setEndMode(e.target.value as 'never' | 'date')}>
                    <option value="never">Never — runs until cancelled</option>
                    <option value="date">On a date</option>
                  </Select>
                </Box>
                {endMode === 'date' && (
                  <Box flex="1">
                    <FormLabel fontSize="sm">End date</FormLabel>
                    <Input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
                  </Box>
                )}
              </HStack>
              {/* item 878 (Chad) — optional notes printed on EVERY monthly invoice: the header
                  note as the memo up top, the footer note at the bottom (both compose with the
                  automatic job-code/project label). */}
              <Box>
                <FormLabel fontSize="sm">Invoice header note (optional)</FormLabel>
                <Textarea
                  size="sm"
                  rows={2}
                  value={headerNote}
                  placeholder="Shown at the top of every monthly invoice"
                  onChange={(e) => setHeaderNote(e.target.value)}
                />
              </Box>
              <Box>
                <FormLabel fontSize="sm">Invoice footer note (optional)</FormLabel>
                <Textarea
                  size="sm"
                  rows={2}
                  value={footerNote}
                  placeholder="Shown at the bottom of every monthly invoice"
                  onChange={(e) => setFooterNote(e.target.value)}
                />
              </Box>
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.500">The SKU lines billed every cycle. Each carries its own monthly amount.</Text>
              {lines.map((l) => (
                <HStack key={l.clientKey} spacing={2} align="center">
                  {/* item 878 (Chad) — the SKU cell searches the Items catalog (pick = fill
                      brand/description + seed the amount) AND still accepts free-typed one-offs. */}
                  <Box flex="1.4">
                    <CatalogCombobox
                      inline
                      value={l.sku}
                      onTextChange={(text) => setLine(l.clientKey, { sku: text })}
                      onPick={(item) => setLine(l.clientKey, {
                        sku: item.sku ?? '',
                        brand: item.brand ?? '',
                        description: item.description ?? '',
                        // seed the monthly amount from the item's group price ONLY when empty
                        monthlyAmount: (Number(l.monthlyAmount) || 0) > 0
                          ? l.monthlyAmount
                          : bindingLinePrice(item) || 0,
                      })}
                      onCreateNew={(typed) => setLine(l.clientKey, { sku: typed })}
                    />
                  </Box>
                  <Input flex="1" size="sm" placeholder="Brand" value={l.brand} onChange={(e) => setLine(l.clientKey, { brand: e.target.value })} />
                  <Input flex="2" size="sm" placeholder="Description" value={l.description} onChange={(e) => setLine(l.clientKey, { description: e.target.value })} />
                  <Flex align="center" w="130px">
                    <Text mr={1} color="gray.500">$</Text>
                    <Input size="sm" type="number" min={0} step="0.01" textAlign="right" placeholder="/mo" value={l.monthlyAmount || ''} onChange={(e) => setLine(l.clientKey, { monthlyAmount: parseFloat(e.target.value) || 0 })} />
                  </Flex>
                  <IconButton aria-label="Remove line" size="sm" variant="ghost" icon={<CloseIcon boxSize={2.5} />} onClick={() => setLines((prev) => (prev.length === 1 ? prev : prev.filter((x) => x.clientKey !== l.clientKey)))} />
                </HStack>
              ))}
              <Button size="sm" variant="outline" leftIcon={<AddIcon boxSize={2.5} />} onClick={() => setLines((prev) => [...prev, blankLine()])}>
                Add line
              </Button>
              <Divider />
              <HStack justify="flex-end">
                <Text fontSize="sm" color="gray.500">Monthly total</Text>
                <Text fontWeight="bold">{money(monthlyTotal)}</Text>
              </HStack>
              <Divider />
              {/* item 1360 — what this subscription DELIVERS; RNWY reads these flags. Required. */}
              <Text fontSize="sm" fontWeight="semibold">Products included (required)</Text>
              <Text fontSize="xs" color="gray.500">Tick what the property is entitled to — this is what the RNWY software reads. SKUs above are hints; your ticks are the truth.</Text>
              <HStack spacing={4} flexWrap="wrap">
                <Checkbox isChecked={prodRnwy} onChange={(e) => { setProdRnwy(e.target.checked); if (e.target.checked) setProdNone(false); }}>RNWY</Checkbox>
                <Checkbox isChecked={prodSp} onChange={(e) => { setProdSp(e.target.checked); if (e.target.checked) setProdNone(false); }}>SwitchPilot</Checkbox>
                <Checkbox isChecked={prodHd} onChange={(e) => { setProdHd(e.target.checked); if (!e.target.checked) setProdHdTier(0); else setProdNone(false); }}>Help Desk</Checkbox>
                <Checkbox isChecked={prodTp} onChange={(e) => { setProdTp(e.target.checked); if (e.target.checked) setProdNone(false); }}>Third-party</Checkbox>
                {/* item 1479 — the generic non-RNWY answer: exclusive with the product ticks */}
                <Checkbox
                  isChecked={prodNone}
                  onChange={(e) => {
                    setProdNone(e.target.checked);
                    if (e.target.checked) { setProdRnwy(false); setProdSp(false); setProdHd(false); setProdHdTier(0); setProdTp(false); }
                  }}
                >
                  Non-RNWY (no entitlements)
                </Checkbox>
              </HStack>
              {prodNone && <Text fontSize="xs" color="gray.500">A plain subscription — Press Play, cloud services, anything RNWY doesn&apos;t read. The property gets no RNWY entitlement from it.</Text>}
              {prodHd && (
                <Select size="sm" maxW="220px" value={prodHdTier} onChange={(e) => setProdHdTier(Number(e.target.value))}>
                  <option value={0}>Help Desk tier…</option>
                  <option value={1}>Gold</option>
                  <option value={2}>Platinum</option>
                  <option value={3}>Titanium</option>
                </Select>
              )}
              {!anyProduct && <Text fontSize="xs" color="orange.300">Pick at least one product — or Non-RNWY — to continue.</Text>}
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.500">
                Recurring commissions are entered here, manually — who earns, on which SKU line,
                at what %. They accrue every month while the subscription is active and stop if it&apos;s
                cancelled. Nothing is auto-assigned.
              </Text>
              {earners.length === 0 && (
                <Text fontSize="sm" color="gray.400">No recurring commissions — add one if someone earns on this subscription.</Text>
              )}
              {earners.map((e, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <HStack key={idx} spacing={2} align="center">
                  <Select flex="2" size="sm" placeholder="— Person —" value={e.userId} onChange={(ev) => setEarner(idx, { userId: ev.target.value })}>
                    {(users?.index ?? []).map((u: UserDto) => (<option key={u.id} value={u.id}>{u.name}</option>))}
                  </Select>
                  <Select flex="2" size="sm" placeholder="— SKU line —" value={e.lineClientKey ?? ''} onChange={(ev) => setEarner(idx, { lineClientKey: ev.target.value })}>
                    {validLines.map((l) => (<option key={l.clientKey} value={l.clientKey}>{lineLabel(l)}</option>))}
                  </Select>
                  <Flex align="center" w="90px">
                    <Input size="sm" type="number" min={0} max={100} step="any" textAlign="right" placeholder="%" value={e.percent || ''} onChange={(ev) => setEarner(idx, { percent: parseFloat(ev.target.value) || 0 })} />
                    <Text ml={1} color="gray.500">%</Text>
                  </Flex>
                  <Text fontSize="sm" w="90px" textAlign="right" color="green.400">{`${money(earnerMonthly(e))}/mo`}</Text>
                  <IconButton aria-label="Remove commission" size="sm" variant="ghost" icon={<CloseIcon boxSize={2.5} />} onClick={() => setEarners((prev) => prev.filter((_, i) => i !== idx))} />
                </HStack>
              ))}
              <Button size="sm" variant="outline" leftIcon={<AddIcon boxSize={2.5} />} onClick={() => setEarners((prev) => [...prev, { userId: '', lineClientKey: validLines[0]?.clientKey ?? null, percent: 0 }])}>
                Add recurring commission
              </Button>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3} w="100%">
            {step > 0 && <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Back</Button>}
            <Badge alignSelf="center" variant="subtle">{`${money(monthlyTotal)}/mo`}</Badge>
            <Box flex="1" />
            <Button variant="ghost" onClick={close}>Cancel</Button>
            {step < 2 && <Button onClick={() => setStep((s) => s + 1)} isDisabled={!canNext}>Next</Button>}
            {step === 2 && <Button onClick={submit} isLoading={create.isLoading} isDisabled={validLines.length === 0}>Create recurring invoice</Button>}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

AddRecurringInvoiceWizard.defaultProps = { lockedProjectId: undefined };

export default AddRecurringInvoiceWizard;
